/**
 * Public Policies Page (no auth)
 *
 * Two routes feed this component:
 *
 *   /policies            → list of all public policies (currently the
 *                          Privacy Policy)
 *   /policies/:policyId  → full body of one public policy
 *   /privacy             → shortcut to /policies/POL-PRIV-001
 *
 * The page is intentionally simple, public, and indexable. No auth, no
 * sidebar. It mirrors the visual language of the Trust Center.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, ExternalLink } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

marked.setOptions({ gfm: true, breaks: true, headerIds: false, mangle: false });

function renderPolicyBody(md) {
  if (!md) return '';
  const html = marked.parse(md);
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function PolicyHeader() {
  return (
    <div className="public-policy-header">
      <div className="public-policy-header-inner">
        <Link to="/" className="public-policy-brand">
          <img src="/threebie-logo.svg" alt="Threebie" />
        </Link>
        <nav className="public-policy-nav">
          <Link to="/legal">Policies</Link>
          <Link to="/trust-center">Trust Center</Link>
          <a href="mailto:security@threebie.com">Contact</a>
        </nav>
      </div>
    </div>
  );
}

function PolicyFooter() {
  return (
    <footer className="public-policy-footer">
      <div className="public-policy-footer-inner">
        <span>© {new Date().getFullYear()} Threebie, LLC. All rights reserved.</span>
        <span className="public-policy-footer-links">
          <Link to="/privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/trust-center">Trust Center</Link>
          <span aria-hidden="true">·</span>
          <a href="mailto:security@threebie.com">security@threebie.com</a>
        </span>
      </div>
    </footer>
  );
}

/**
 * Index page — list every public policy.
 */
export function PublicPoliciesListPage() {
  const [data, setData] = useState({ total: 0, policies: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/public/policies`)
      .then((r) => {
        if (!r.ok) throw new Error('Could not load policies');
        return r.json();
      })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="public-policy-page">
      <PolicyHeader />
      <main className="public-policy-main">
        <div className="public-policy-shell">
          <div className="public-policy-eyebrow">
            <Shield size={14} />
            Public Policies
          </div>
          <h1>Threebie Public Policies</h1>
          <p className="public-policy-lede">
            Threebie publishes the policies below for prospective and current
            users. Internal procedures and SOC 2 evidence are available to
            verified clients via the <Link to="/trust-center">Trust Center</Link>.
          </p>

          {loading && <div className="public-policy-loading">Loading policies…</div>}
          {error && <div className="public-policy-error">{error}</div>}

          {!loading && !error && data.total === 0 && (
            <div className="public-policy-empty">
              No public policies available right now. Visit the{' '}
              <Link to="/trust-center">Trust Center</Link> to request access to
              detailed compliance documentation.
            </div>
          )}

          {!loading && !error && data.policies.length > 0 && (
            <ul className="public-policy-list">
              {data.policies.map((p) => (
                <li key={p.policy_id} className="public-policy-card">
                  <Link to={`/legal/${p.policy_id}`} className="public-policy-card-link">
                    <div className="public-policy-card-icon">
                      <FileText size={20} />
                    </div>
                    <div className="public-policy-card-body">
                      <div className="public-policy-card-title">{p.title}</div>
                      {p.summary && (
                        <div className="public-policy-card-summary">{p.summary}</div>
                      )}
                      <div className="public-policy-card-meta">
                        <span>Version {p.version}</span>
                        {p.last_updated && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>Updated {formatDate(p.last_updated)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="public-policy-card-cta" aria-hidden="true">→</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <PolicyFooter />
    </div>
  );
}

/**
 * Single-policy page.
 *
 * The privacy policy is special-cased: under no circumstance should /privacy
 * surface a "not available publicly" screen. If the API misbehaves we retry
 * a couple of times and, as a last resort, show a minimal plain-text policy
 * pointer so public visitors always land on something useful.
 */
export function PublicPolicyDetailPage() {
  const { policyId: paramPolicyId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Allow /privacy (no param) to act as a shortcut to POL-PRIV-001
  const policyId = paramPolicyId || 'POL-PRIV-001';
  const isPrivacyRoute = !paramPolicyId || policyId === 'POL-PRIV-001';

  useEffect(() => {
    let cancelled = false;
    // Reset state cleanly when the policyId changes — prevents a stale
    // error from a previous route (e.g. /legal/POL-AC-001) from leaking
    // into the rendered view of a legitimately-public policy.
    setLoading(true);
    setError(null);
    setPolicy(null);

    // The global fetch interceptor in App.js automatically bypasses the
    // Authorization header for any `/api/public/*` path, so this plain
    // fetch is guaranteed to be anonymous even if the visitor still has a
    // stale token in localStorage from a previous staff session.
    const fetchWithRetry = async (attempt = 0) => {
      try {
        // Cache-bust via a once-per-request query param so intermediate
        // caches can't pin us to a stale 404.
        const url = `${API_URL}/api/public/policies/${policyId}?t=${Date.now()}`;
        const r = await fetch(url, { headers: { Accept: 'application/json' } });
        if (r.status === 404) {
          throw Object.assign(new Error('not_found'), { status: 404 });
        }
        if (!r.ok) throw Object.assign(new Error('failed'), { status: r.status });
        const data = await r.json();
        if (!cancelled) {
          setPolicy(data);
          setError(null);
          setLoading(false);
        }
      } catch (e) {
        // Retry transient failures up to 2 times before giving up — keeps
        // the public Privacy Policy resilient to fetch blips.
        if (e.status !== 404 && attempt < 2) {
          setTimeout(() => { if (!cancelled) fetchWithRetry(attempt + 1); }, 400 * (attempt + 1));
          return;
        }
        if (!cancelled) {
          setError(e.message || 'failed');
          setLoading(false);
        }
      }
    };

    fetchWithRetry();
    return () => { cancelled = true; };
  }, [policyId]);

  // Set <title> for nicer browser tabs and accessible navigation
  useEffect(() => {
    if (policy?.title) {
      document.title = `${policy.title} — Threebie`;
    } else {
      document.title = 'Threebie';
    }
    return () => { document.title = 'Threebie Compliance Platform'; };
  }, [policy]);

  const html = useMemo(
    () => renderPolicyBody(policy?.content_markdown),
    [policy?.content_markdown]
  );

  return (
    <div className="public-policy-page">
      <PolicyHeader />
      <main className="public-policy-main">
        <div className="public-policy-shell">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="public-policy-back"
          >
            <ArrowLeft size={14} /> Back
          </button>

          {loading && <div className="public-policy-loading">Loading…</div>}

          {/* Privacy-route fallback — NEVER show "not available" for /privacy.
              If the API genuinely fails, present a pared-down but complete
              public-facing page pointing to the live policy + contact. */}
          {!loading && error && isPrivacyRoute && (
            <div className="public-policy-article" data-testid="privacy-policy-fallback">
              <header className="public-policy-article-header">
                <div className="public-policy-eyebrow">
                  <Shield size={14} />
                  Privacy
                </div>
                <h1>Privacy Policy</h1>
                <div className="public-policy-meta-row">
                  <span><strong>Publisher:</strong> Threebie, LLC.</span>
                </div>
              </header>
              <div className="public-policy-body rich-editor-content">
                <p>
                  We couldn't load the most recent version of our Privacy Policy
                  right now. This is a temporary issue — please refresh in a
                  moment or use one of the links below.
                </p>
                <ul>
                  <li>
                    Email our privacy team at{' '}
                    <a href="mailto:privacy@threebie.com">privacy@threebie.com</a>{' '}
                    for a copy of the current Privacy Policy.
                  </li>
                  <li>
                    See Threebie's{' '}
                    <Link to="/trust-center">Trust Center</Link> for additional
                    compliance and security documentation.
                  </li>
                  <li>
                    Browse <Link to="/legal">all published Threebie policies</Link>.
                  </li>
                </ul>
                <p>
                  Threebie complies with the GDPR, the California Consumer
                  Privacy Act (CCPA/CPRA), and other applicable US state and
                  international privacy laws. You have the right to access,
                  correct, delete, and port your personal information. To
                  exercise any of these rights, contact{' '}
                  <a href="mailto:privacy@threebie.com">privacy@threebie.com</a>.
                </p>
              </div>
              <div className="public-policy-questions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => window.location.reload()}
                  data-testid="privacy-retry-btn"
                >
                  Retry loading full Privacy Policy
                </button>
              </div>
            </div>
          )}

          {/* Non-privacy routes — if someone lands on a policy that isn't
              published publicly, guide them to the policies that ARE public
              instead of presenting a dead-end error page. */}
          {!loading && error === 'not_found' && !isPrivacyRoute && (
            <div className="public-policy-error" data-testid="policy-not-public">
              <h1>That document isn't published publicly</h1>
              <p>
                Threebie publishes a curated set of policies on this site. The
                document you requested isn't one of them. You can still:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: 22, lineHeight: 1.7 }}>
                <li>Read our <Link to="/privacy">Privacy Policy</Link></li>
                <li>Browse <Link to="/legal">all public policies</Link></li>
                <li>Visit the <Link to="/trust-center">Trust Center</Link> to request
                  detailed compliance documentation under NDA</li>
                <li>Email <a href="mailto:security@threebie.com">security@threebie.com</a> with
                  specific questions</li>
              </ul>
            </div>
          )}

          {!loading && error && error !== 'not_found' && !isPrivacyRoute && (
            <div className="public-policy-error" data-testid="policy-load-error">
              <h1>Couldn't load this policy</h1>
              <p>
                Please try again in a moment, or email{' '}
                <a href="mailto:security@threebie.com">security@threebie.com</a>.
              </p>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => window.location.reload()}
                style={{ marginTop: 12 }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && policy && (
            <article className="public-policy-article">
              <header className="public-policy-article-header">
                <div className="public-policy-eyebrow">
                  <Shield size={14} />
                  {policy.category ? policy.category.replace(/_/g, ' ') : 'Policy'}
                </div>
                <h1>{policy.title}</h1>
                <div className="public-policy-meta-row">
                  <span><strong>Version:</strong> {policy.version}</span>
                  {policy.last_updated && (
                    <span><strong>Last updated:</strong> {formatDate(policy.last_updated)}</span>
                  )}
                  {policy.next_review && (
                    <span><strong>Next review:</strong> {formatDate(policy.next_review)}</span>
                  )}
                </div>
              </header>

              <div
                className="public-policy-body rich-editor-content"
                dangerouslySetInnerHTML={{ __html: html }}
              />

              <div className="public-policy-questions">
                <h2>Questions about this policy?</h2>
                <p>
                  Email <a href="mailto:security@threebie.com">security@threebie.com</a>.
                  For broader compliance documentation, visit our{' '}
                  <Link to="/trust-center">Trust Center <ExternalLink size={12} /></Link>.
                </p>
              </div>
            </article>
          )}
        </div>
      </main>
      <PolicyFooter />
    </div>
  );
}

export default PublicPoliciesListPage;
